import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useAirQuality, getAirQualityLevel, getAirQualityColor } from '@/hooks/useAirQuality';
import { Leaf, MapPin } from 'lucide-react';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';

export default function AirQualityPage() {
  const { language } = useAppLanguage();
  const airQualityQuery = useAirQuality();

  if (airQualityQuery.isLoading) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('air', language)} />
        <div style={styles.content}>
          <div style={styles.loadingMessage}>Loading air quality data...</div>
        </div>
      </DetailPageLayout>
    );
  }

  if (airQualityQuery.isError || !airQualityQuery.data) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('air', language)} />
        <div style={styles.content}>
          <div style={styles.errorMessage}>
            Unable to load air quality data. Please try again later.
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  const data = airQualityQuery.data;
  const pm25Value = data.parameters.pm25?.value;
  const airQualityLevel = pm25Value !== undefined ? getAirQualityLevel(pm25Value) : null;

  const levelLabels = {
    good: { en: 'Good', ru: 'Хорошее', pl: 'Dobre' },
    moderate: { en: 'Moderate', ru: 'Умеренное', pl: 'Umiarkowane' },
    unhealthy: { en: 'Unhealthy', ru: 'Нездоровое', pl: 'Niezdrowe' },
    hazardous: { en: 'Hazardous', ru: 'Опасное', pl: 'Niebezpieczne' },
  };

  return (
    <DetailPageLayout>
      <DetailPageHeader title={getTranslation('air', language)} />

      <div style={styles.content}>
        <div style={styles.mainCard}>
          <div style={styles.header}>
            <Leaf size={24} color={Colors.primary} />
            <h2 style={styles.title}>Air Quality</h2>
          </div>

          <div style={styles.stationInfo}>
            <MapPin size={14} color={Colors.textSecondary} />
            <span style={styles.stationName}>{data.stationName}</span>
          </div>

          {pm25Value !== undefined && airQualityLevel && (
            <div style={styles.mainReading}>
              <div
                style={{
                  ...styles.mainValue,
                  color: getAirQualityColor(airQualityLevel),
                }}
              >
                {pm25Value}
              </div>
              <div style={styles.mainLabel}>PM2.5</div>
              <div
                style={{
                  ...styles.levelBadge,
                  backgroundColor: getAirQualityColor(airQualityLevel),
                }}
              >
                {levelLabels[airQualityLevel][language]}
              </div>
            </div>
          )}
        </div>

        <div style={styles.parametersCard}>
          <h3 style={styles.sectionTitle}>
            {language === 'ru' ? 'Все параметры' : language === 'pl' ? 'Wszystkie parametry' : 'All Parameters'}
          </h3>
          <div style={styles.parametersGrid}>
            {data.parameters.pm25 && (
              <div style={styles.paramItem}>
                <div style={styles.paramLabel}>PM2.5</div>
                <div
                  style={{
                    ...styles.paramValue,
                    color: getAirQualityColor(getAirQualityLevel(data.parameters.pm25.value)),
                  }}
                >
                  {data.parameters.pm25.value}
                </div>
                <div style={styles.paramUnit}>{data.parameters.pm25.unit}</div>
              </div>
            )}
            {data.parameters.pm10 && (
              <div style={styles.paramItem}>
                <div style={styles.paramLabel}>PM10</div>
                <div style={styles.paramValue}>{data.parameters.pm10.value}</div>
                <div style={styles.paramUnit}>{data.parameters.pm10.unit}</div>
              </div>
            )}
            {data.parameters.h2s && (
              <div style={styles.paramItem}>
                <div style={styles.paramLabel}>H2S</div>
                <div style={styles.paramValue}>{data.parameters.h2s.value}</div>
                <div style={styles.paramUnit}>{data.parameters.h2s.unit}</div>
              </div>
            )}
            {data.parameters.so2 && (
              <div style={styles.paramItem}>
                <div style={styles.paramLabel}>SO2</div>
                <div style={styles.paramValue}>{data.parameters.so2.value}</div>
                <div style={styles.paramUnit}>{data.parameters.so2.unit}</div>
              </div>
            )}
            {data.parameters.no2 && (
              <div style={styles.paramItem}>
                <div style={styles.paramLabel}>NO2</div>
                <div style={styles.paramValue}>{data.parameters.no2.value}</div>
                <div style={styles.paramUnit}>{data.parameters.no2.unit}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ height: '40px' }} />
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    padding: '24px',
  } as React.CSSProperties,

  loadingMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: Colors.textSecondary,
    fontSize: '16px',
  } as React.CSSProperties,

  errorMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: '#d32f2f',
    fontSize: '16px',
  } as React.CSSProperties,

  mainCard: {
    backgroundColor: Colors.surface,
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  } as React.CSSProperties,

  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: Colors.text,
    margin: 0,
  } as React.CSSProperties,

  stationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '20px',
  } as React.CSSProperties,

  stationName: {
    fontSize: '14px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  mainReading: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '16px',
  } as React.CSSProperties,

  mainValue: {
    fontSize: '56px',
    fontWeight: '700',
  } as React.CSSProperties,

  mainLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.textSecondary,
    marginTop: '4px',
  } as React.CSSProperties,

  levelBadge: {
    marginTop: '12px',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
  } as React.CSSProperties,

  parametersCard: {
    backgroundColor: Colors.surface,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: Colors.text,
    margin: 0,
    marginBottom: '16px',
  } as React.CSSProperties,

  parametersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  } as React.CSSProperties,

  paramItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '16px 8px',
    backgroundColor: Colors.primaryLight,
    borderRadius: '12px',
  } as React.CSSProperties,

  paramLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: Colors.textSecondary,
    marginBottom: '4px',
  } as React.CSSProperties,

  paramValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: Colors.text,
  } as React.CSSProperties,

  paramUnit: {
    fontSize: '10px',
    color: Colors.textSecondary,
    marginTop: '2px',
  } as React.CSSProperties,
} as const;
